<template>
  <li
    ref="elRoot"
    class="PmSearchResultPure"
    :class="classes.root"
    tabindex="1"
    @click="$emit('click')"
  >
    <header class="PmSearchResultPure-header">
      <PmStatusPillPure
        v-if="number && jobStatus"
        :label="number"
        type="job"
        :job-status="jobStatus"
      />

      <PmStatusPillPure
        v-else-if="number && purchaseOrderStatus"
        :label="number"
        type="purchaseOrder"
        :purchase-order-status="purchaseOrderStatus"
      />

      <PmPillPure v-else-if="number" :label="number" />

      <div class="PmSearchResultPure-title">{{ title }}</div>

      <div
        v-if="isCollectedWithAllJobsOfProject"
        :title="`Job wird zusammen mit anderen Jobs des Projekts ${projectNumber} zusammengefasst angezeigt`"
        class="PmSearchResultPure-icon"
      >
        <PmIconPure :name="ICONS_SMALL.COLLECTION" />
      </div>

      <div class="PmSearchResultPure-meta">
        <PmLoadingPure
          size="smallest"
          class="PmSearchResultPure-loader"
          :class="{ 'is-visible': loading }"
        />

        <PmPillPure
          class="PmSearchResultPure-type"
          :label="TYPE_LOOKUP_LABEL[type]"
        />
      </div>
    </header>

    <div class="PmSearchResultPure-details">
      <template v-if="project">
        Projekt: {{ project }}
        <br />
      </template>

      <template v-if="projectLeader">
        Leiter: {{ projectLeader }}
        <br />
      </template>
      {{ TYPE_LOOKUP_DATE_LABEL[type] }}:
      <template v-if="isSameDay(startDate, endDate)">
        {{ formatWithLocale(startDate, FORMAT_DATE_DEFAULT) }}
      </template>
      <template v-else>
        {{ formatWithLocale(startDate, FORMAT_DATE_DEFAULT) }} —
        {{ formatWithLocale(endDate, FORMAT_DATE_DEFAULT) }}
      </template>
    </div>

    <PmAppNotificationPure
      v-if="warning"
      variant="warning"
      class="PmSearchResultPure-warning"
    >
      {{ warning }}
    </PmAppNotificationPure>
  </li>
</template>

<script lang="ts">
import { defineComponent, onMounted, onBeforeUnmount } from 'vue'

const COMPONENT_NAME = 'ExampleComponentBarebones'

export const propTypes = {} as const

export default defineComponent({
  name: COMPONENT_NAME,
})
</script>

<script setup lang="ts">
import { computed, watch, ref } from 'vue'
import { differenceInCalendarDays, isSameDay } from 'date-fns'

import { ICONS_SMALL } from '@/constants/icons'
import {
  FORMAT_DATE_DEFAULT,
  type CalendarItemType,
} from '@/constants/persoplan'
import { formatWithLocale } from '@/utilities/date'

import PmLoadingPure from '@/components/basics/PmLoading/PmLoadingPure.vue'
import PmAppNotificationPure from '@/components/basics/PmAppNotification/PmAppNotificationPure.vue'
import PmIconPure from '@/components/basics/PmIcon/PmIconPure.vue'
import PmPillPure from '@/components/basics/PmPillPure.vue'
import PmStatusPillPure from '@/components/persoplan/PmStatusPill/PmStatusPillPure.vue'

import type { JobStatus, PurchaseOrderStatus } from '@/constants/persoplan'
import type { Nilable } from '@/types/misc'

export interface Props {
  title: string
  type: CalendarItemType
  number?: string
  jobStatus?: Nilable<JobStatus>
  purchaseOrderStatus?: Nilable<PurchaseOrderStatus>
  startDate: Date
  endDate: Date
  project?: string
  projectNumber?: string
  projectLeader?: Nilable<string>
  loading?: boolean
  warning?: string
  isActive?: boolean
  now?: Date
  isCollectedWithAllJobsOfProject?: Nilable<boolean>
}

const TYPE_LOOKUP_LABEL = {
  project: 'Projekt',
  job: 'Job',
  stockTypeService: 'Werkstattvorgang',
  purchaseOrder: 'Bestellung',
} as const satisfies Record<CalendarItemType, string>

const TYPE_LOOKUP_DATE_LABEL = {
  project: 'Zeitraum',
  job: 'Zeitraum',
  stockTypeService: 'Zeitraum',
  purchaseOrder: 'Bestelldatum',
} as const satisfies Record<CalendarItemType, string>

const props = withDefaults(defineProps<Props>(), {
  now: () => new Date(),
})

const emit = defineEmits<{
  (event: 'click'): void
}>()

const elRoot = ref<HTMLLIElement>()

const daysToNow = computed(() => {
  return Math.abs(differenceInCalendarDays(props.startDate, props.now))
})

const classes = computed(() => {
  return {
    root: {
      'is-focus': props.isActive,
      'is-quiet': daysToNow.value > 365, // TODO: This should be a prop and be set where it's used maybe?
    },
  }
})

watch(() => props.isActive, maybeSetFocus)
onMounted(maybeSetFocus)

// this.$eventHub.$on('search.resetResultsStatus', this.reset) // TODO
onBeforeUnmount(() => {
  // this.$eventHub.$off('search.resetResultsStatus', this.reset) // TODO
})

function maybeSetFocus() {
  if (!props.isActive) return
  if (!elRoot.value) return

  elRoot.value.scrollIntoView({
    // behavior: 'smooth',
    block: 'nearest',
    // inline: 'nearest'
  })
}
</script>

<style lang="scss">
.PmSearchResultPure {
  $block: &;
  $name: function.getBlockName($block);

  list-style-type: none;
  margin: 0;
  padding: 8px;
  border: 2px solid transparent;
  border-radius: constant.$borderRadius-default;
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  grid-template-areas:
    'header header'
    'details details'
    'warning warning';

  &:hover,
  &.is-hover {
    opacity: 1;
    background-color: rgba(color.$key, 0.2);
  }

  &:focus,
  &.is-focus {
    outline: none;
    opacity: 1;
    border-color: color.$key;
    background-color: rgba(color.$key, 0.2);
  }

  &.is-quiet {
    opacity: 0.5;
  }

  &-header {
    grid-area: header;
    display: flex;
    align-items: center;
    gap: 4px;
    min-height: 20px;
    margin-bottom: 0.5em;
  }

  &-icon {
    width: 20px;
    height: 20px;
  }

  &-title {
    font-size: constant.$fontSize-large;
    font-weight: 600;
    display: flex;
    align-items: baseline;
  }

  &-meta {
    margin-left: auto;
    display: flex;
    gap: 4px;
  }

  &-loader {
    align-self: center;
    display: none;

    &.is-visible {
      display: block;
    }
  }

  &-details {
    grid-area: details;
  }

  &-warning {
    grid-area: warning;
    margin-top: var(--space-gutters);
  }
}
</style>
